import React, {
  useCallback,
  useMemo,
  useState,
}                              from 'react';
import {
  ICreditUnion,
  ICarFaxUser,
  IPartners,
  IPartnersInfo,
}                              from '@typings/accountManagement';
import {
  EPartnersStateType,
  EConnectServiceState,
}                              from '@enums/accountManagement';
import {
  useFetchPartnersState,
  useAuthorizeToCarFax,
  useUnauthorizeFromCarFax,
  useFetchReindeerState,
  useUpdateReindeerState,
}                              from '@modules/accountManagement/queries/accountQueries';
import { AccountPartnersPage } from '@modules/accountManagement/pages/AccountPartnersPage';
import { Loader }              from '@components/Loader/Loader';

const formatPartners = (partnersInfo: IPartnersInfo): IPartners => [{
  type    : EPartnersStateType.SRS,
  state   : partnersInfo.srsState,
  accName : partnersInfo.srsUserName,
}, {
  type    : EPartnersStateType.ER,
  state   : partnersInfo.erState,
  accName : partnersInfo.erFolder ? `${partnersInfo.erFolder} ${partnersInfo.erLenderId}` : '',
}, {
  type    : EPartnersStateType.CarFaxAuth,
  state   : partnersInfo.carFaxAuthState,
  accName : partnersInfo.carFaxUserName,
}, {
  type  : EPartnersStateType.CarFaxActivate,
  state : partnersInfo.carFaxActivateState,
}, {
  type  : EPartnersStateType.DealerOnly,
  state : partnersInfo.dealerOnlyState,
}, {
  type  : EPartnersStateType.DeficiencyTracking,
  state : partnersInfo.deficiencyTracking,
}, {
  type    : EPartnersStateType.VRM,
  state   : partnersInfo.vrmState,
  accName : partnersInfo.vrmLienHolder,
}, {
  type  : EPartnersStateType.Reindeer,
  state : partnersInfo.reindeerState,
}];

interface IProps {
  push        : (path: string) => void;
  accountData : ICreditUnion;
  cuid        : string;
}

export const AccountPartnersContainer: React.FC<IProps> = ({
  push,
  accountData,
  cuid,
}) => {
  const [reindeerState, setReindeerState] = useState<EConnectServiceState>(
    EConnectServiceState.Removed,
  );

  // #region API INTEGRATION
  const {
    refetch: refetchPartnersState,
    data = {} as IPartnersInfo,
    isLoading,
  } = useFetchPartnersState(cuid, {
    enabled: !!cuid,
  });

  const onSuccess = useCallback(
    async (): Promise<void> => {
      await refetchPartnersState({ throwOnError: true });
    },
    [refetchPartnersState],
  );

  const {
    mutateAsync : authorize,
    isLoading   : isLoadingAuthorize,
  } = useAuthorizeToCarFax({ onSuccess });

  const {
    mutateAsync : unauthorize,
    isLoading   : isLoadingUnauthorize,
  } = useUnauthorizeFromCarFax({ onSuccess });

  useFetchReindeerState(cuid, {
    enabled   : !!cuid,
    onSuccess : (state) => setReindeerState(state),
  });

  const {
    mutateAsync : updateReindeerState,
    isLoading   : isLoadingReindeer,
  } = useUpdateReindeerState({
    onSuccess: async () => {
      await refetchPartnersState({ throwOnError: true });
    },
  });
  // #endregion

  const authorizeToCarFax = useCallback(
    async ({ userName }: ICarFaxUser): Promise<void> => {
      await authorize({ userName, cuid });
    },
    [authorize, cuid],
  );

  const unauthorizeFromCarFax = useCallback(
    async (): Promise<void> => {
      await unauthorize({ cuid });
    },
    [unauthorize, cuid],
  );

  const onReindeerAuthClick = useCallback(
    async (): Promise<void> => {
      const isAuth: boolean = reindeerState !== EConnectServiceState.Authorized;

      await updateReindeerState({ isAuth, cuid });

      setReindeerState(isAuth ? EConnectServiceState.Authorized : EConnectServiceState.Removed);
    },
    [cuid, reindeerState, updateReindeerState],
  );

  const formattedPartners = useMemo<IPartners>(
    () => formatPartners(data),
    [data],
  );

  const reindeerBtnLabel = useMemo<string>(
    () => (reindeerState === EConnectServiceState.Pending ? 'Authorize' : 'Unauthorize'),
    [reindeerState],
  );

  if (isLoading) {
    return <Loader />;
  }

  return (
    <AccountPartnersPage
      partners              = {formattedPartners}
      authorizeToCarFax     = {authorizeToCarFax}
      unauthorizeFromCarFax = {unauthorizeFromCarFax}
      isLoading             = {isLoading || isLoadingAuthorize || isLoadingUnauthorize}
      carFaxAuthState       = {data.carFaxAuthState}
      push                  = {push}
      accountData           = {accountData}
      reindeerState         = {reindeerState}
      reindeerBtnLabel      = {reindeerBtnLabel}
      onReindeerAuthClick   = {onReindeerAuthClick}
      isLoadingReindeer     = {isLoadingReindeer}
    />
  );
};
